/**
 * LegacyMarkBilledDialog
 *
 * Shared dialog for marking one or more notes as "billed in a legacy system"
 * (creates `billed_legacy` stub claims so ERA payments can later be matched
 * to services without generating an 837P).
 *
 * Used by:
 *   • BillingTab bulk flow — multi-select, mark many at once.
 *   • Prepare Claim dialog (App.js) — single-note case, pre-selected.
 *
 * The parent owns `open`, `noteIds`, and the `onSuccess` callback so each
 * surface can run its own post-success housekeeping (list removal, event
 * dispatch, etc.). This component is purely form + submit; it doesn't track
 * which notes are selected.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Archive, Check, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function LegacyMarkBilledDialog({
  open,
  onOpenChange,
  noteIds = [],
  onSuccess,
  // Optional — when true, skips the "default CPT / charge" section because
  // the caller already knows the single note has everything it needs.
  compact = false,
}) {
  const [refNumber, setRefNumber] = useState("");
  const [systemName, setSystemName] = useState("");
  const [submissionDate, setSubmissionDate] = useState("");
  const [defaultCpt, setDefaultCpt] = useState("");
  const [defaultCharge, setDefaultCharge] = useState("");
  const [inProgress, setInProgress] = useState(false);

  const resetForm = () => {
    setRefNumber("");
    setSystemName("");
    setSubmissionDate("");
    setDefaultCpt("");
    setDefaultCharge("");
  };

  const handleOpenChange = (next) => {
    if (inProgress) return;
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!noteIds || noteIds.length === 0) return;
    setInProgress(true);
    try {
      const payload = {
        note_ids: noteIds,
        legacy_reference_number: refNumber.trim() || null,
        legacy_system_name: systemName.trim() || null,
        legacy_submission_date: submissionDate || null,
        default_cpt_code: defaultCpt.trim() || null,
        default_charge_amount: defaultCharge ? parseFloat(defaultCharge) : null,
      };
      const resp = await axios.post(
        `${API}/billing/notes/bulk-mark-legacy-billed`,
        payload
      );
      const data = resp.data || {};
      const { summary, skipped, errors } = data;

      if (summary?.created > 0) {
        toast.success(
          `Created ${summary.created} legacy stub claim${summary.created > 1 ? "s" : ""}` +
            (summary.skipped ? ` · ${summary.skipped} skipped` : "") +
            (summary.errors
              ? ` · ${summary.errors} error${summary.errors > 1 ? "s" : ""}`
              : "")
        );
      } else if (summary?.skipped > 0 && summary?.created === 0) {
        toast.warning(
          `All ${summary.skipped} note${summary.skipped > 1 ? "s were" : " was"} skipped (claim already exists)`
        );
      } else if (summary?.errors > 0) {
        toast.error(
          `Failed to create stubs: ${summary.errors} error${summary.errors > 1 ? "s" : ""}`
        );
      }

      if (errors && errors.length > 0) {
        console.error("[legacy bulk-mark] errors:", errors);
        const firstErr = errors[0]?.error || "Unknown error";
        toast.error(
          errors.length === 1
            ? firstErr
            : `${firstErr} (and ${errors.length - 1} more — see console)`,
          { duration: 8000 }
        );
      }
      if (skipped && skipped.length > 0) {
        console.log("[legacy bulk-mark] skipped:", skipped);
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
      resetForm();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to mark as legacy-billed"
      );
    } finally {
      setInProgress(false);
    }
  };

  const count = noteIds.length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[520px]"
        data-testid="legacy-mark-dialog-shared"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-purple-700" />
            Mark as Billed in Legacy System
          </DialogTitle>
          <DialogDescription>
            Creates a stub claim per selected note (status{" "}
            <code className="bg-purple-100 px-1 rounded">billed_legacy</code>)
            so ERA payments can be matched back to the services. The original
            note{count > 1 ? "s" : ""} will be locked. This does{" "}
            <strong>not</strong> generate an 837P or submit anything to a payer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="p-2 bg-gray-50 border rounded text-sm">
            <span className="font-medium">{count}</span> note
            {count > 1 ? "s" : ""} will be marked as billed in the prior
            system.
          </div>

          <div>
            <Label htmlFor="legacy-system-name-shared" className="text-sm">
              Legacy system name{" "}
              <span className="text-gray-400 text-xs">(optional)</span>
            </Label>
            <Input
              id="legacy-system-name-shared"
              placeholder="e.g., TherapyNotes, SimplePractice, AdvancedMD"
              value={systemName}
              onChange={(e) => setSystemName(e.target.value)}
              data-testid="legacy-system-name-input-shared"
            />
          </div>

          <div>
            <Label htmlFor="legacy-submission-date-shared" className="text-sm">
              Original submission date{" "}
              <span className="text-gray-400 text-xs">
                (optional — for timely-filing tracking)
              </span>
            </Label>
            <Input
              id="legacy-submission-date-shared"
              type="date"
              value={submissionDate}
              onChange={(e) => setSubmissionDate(e.target.value)}
              data-testid="legacy-submission-date-input-shared"
            />
          </div>

          <div>
            <Label htmlFor="legacy-ref-number-shared" className="text-sm">
              Legacy reference number{" "}
              <span className="text-gray-400 text-xs">
                (optional{count > 1 ? " — applied to all selected" : ""})
              </span>
            </Label>
            <Input
              id="legacy-ref-number-shared"
              placeholder="e.g., BATCH-2026-03"
              value={refNumber}
              onChange={(e) => setRefNumber(e.target.value)}
              data-testid="legacy-ref-number-input-shared"
            />
            {count > 1 && (
              <p className="text-xs text-gray-500 mt-1">
                Same reference is stamped on every stub in this batch. Leave
                blank if you don&apos;t have per-note reference numbers.
              </p>
            )}
          </div>

          {!compact && (
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Default CPT &amp; charge{" "}
                <span className="font-normal text-gray-400 normal-case">
                  (for notes that have no CPT set)
                </span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label
                    htmlFor="legacy-default-cpt-shared"
                    className="text-sm"
                  >
                    Default CPT code
                  </Label>
                  <Input
                    id="legacy-default-cpt-shared"
                    placeholder="e.g., 90834"
                    value={defaultCpt}
                    onChange={(e) => setDefaultCpt(e.target.value)}
                    data-testid="legacy-default-cpt-input-shared"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="legacy-default-charge-shared"
                    className="text-sm"
                  >
                    Default charge ($)
                  </Label>
                  <Input
                    id="legacy-default-charge-shared"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g., 120.00"
                    value={defaultCharge}
                    onChange={(e) => setDefaultCharge(e.target.value)}
                    data-testid="legacy-default-charge-input-shared"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Only applied when a note has no CPT/appointment. Notes that
                already have a CPT keep theirs.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={inProgress}
            data-testid="legacy-mark-cancel-shared"
          >
            Cancel
          </Button>
          <Button
            className="bg-purple-700 hover:bg-purple-800 text-white"
            onClick={handleSubmit}
            disabled={inProgress || count === 0}
            data-testid="legacy-mark-confirm-shared"
          >
            {inProgress ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Creating
                stub{count > 1 ? "s" : ""}…
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" /> Create {count} stub claim
                {count > 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LegacyMarkBilledDialog;
